/**
 * PostMessage protocol for iframe-parent communication
 *
 * This module defines a type-safe protocol for authentication messages
 * between the iframe (ui-frontend) and the parent website.
 */

import { getIframeContext, isAllowedParentOrigin } from './context';
import logger from '@/lib/logger';

/**
 * Message types for iframe authentication protocol
 */
export type IframeMessageType =
  /** Iframe requests auth tokens from parent */
  | 'AUTH_TOKEN_REQUEST'
  /** Parent sends auth tokens to iframe */
  | 'AUTH_TOKEN_RESPONSE'
  /** Iframe notifies parent of auth state changes */
  | 'AUTH_STATE_CHANGE'
  /** Iframe requests parent to handle logout */
  | 'AUTH_LOGOUT_REQUEST'
  /** Iframe requests parent to initiate login (OAuth popup) */
  | 'AUTH_LOGIN_REQUEST'
  /** Iframe signals it's ready to receive messages */
  | 'IFRAME_READY';

/**
 * Base interface for all iframe messages
 */
export interface IframeMessage {
  /** Message type identifier */
  type: IframeMessageType;
  /** Optional payload data */
  payload?: unknown;
  /** Unique request ID for correlation */
  requestId?: string;
}

/**
 * Payload for AUTH_TOKEN_RESPONSE messages
 */
export interface AuthTokenPayload {
  /** Supabase access token (JWT) */
  accessToken: string;
  /** Supabase refresh token */
  refreshToken: string;
  /** Optional token expiry timestamp (Unix ms) */
  expiresAt?: number;
}

/**
 * Payload for AUTH_STATE_CHANGE messages
 */
export interface AuthStatePayload {
  /** Whether user is authenticated */
  isAuthenticated: boolean;
  /** User ID if authenticated */
  userId?: string;
  /** User email if authenticated */
  email?: string;
  /** User role if authenticated */
  role?: string;
}

/**
 * Namespace prefix for all iframe auth messages
 * Used to filter out unrelated postMessage events
 */
export const MESSAGE_NAMESPACE = 'neutralaiz:iframe:auth';

/**
 * Create a new iframe message with unique request ID
 *
 * @param type - Message type
 * @param payload - Optional payload data
 * @returns IframeMessage object ready to send
 */
export function createMessage(type: IframeMessageType, payload?: unknown): IframeMessage {
  // Generate unique ID using crypto if available, fallback to timestamp
  const requestId = typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;

  return {
    type,
    payload,
    requestId,
  };
}

/**
 * Structure of a message as sent over postMessage
 */
interface PostMessagePayload extends IframeMessage {
  namespace: string;
}

/**
 * Send a message to the parent window
 *
 * SOC2: Messages are sent ONLY to validated parent origins, never with wildcard '*'.
 * This prevents auth tokens from being leaked to malicious parent frames.
 *
 * @param message - The message to send
 */
export function sendToParent(message: IframeMessage): void {
  if (typeof window === 'undefined') return;

  // Don't send if we're not in an iframe
  if (window.self === window.top) return;

  // SOC2: Get and validate parent origin before sending ANY message
  const context = getIframeContext();
  const targetOrigin = context.parentOrigin;

  // SOC2: NEVER send messages to unknown parents - prevents token leakage
  if (!targetOrigin) {
    logger.warn('Messages', 'Cannot send message: parent origin unknown');
    return;
  }

  // SOC2: Only send to allowed origins
  if (!isAllowedParentOrigin(targetOrigin)) {
    logger.warn('Messages', 'Cannot send message: parent origin not allowed', { targetOrigin });
    return;
  }

  const payload: PostMessagePayload = {
    namespace: MESSAGE_NAMESPACE,
    ...message,
  };

  // SOC2: Send to validated parent origin ONLY, never '*'
  window.parent.postMessage(payload, targetOrigin);
}

/**
 * Parse and validate an incoming postMessage event
 *
 * @param event - MessageEvent from window.addEventListener('message', ...)
 * @returns Parsed IframeMessage or null if invalid/not our message
 */
export function parseMessage(event: MessageEvent): IframeMessage | null {
  const data = event.data;

  // Must be an object
  if (!data || typeof data !== 'object') return null;

  // Must have our namespace
  if (data.namespace !== MESSAGE_NAMESPACE) return null;

  // Must have a valid type
  if (!data.type || typeof data.type !== 'string') return null;

  return {
    type: data.type as IframeMessageType,
    payload: data.payload,
    requestId: data.requestId,
  };
}

/**
 * Type guard to validate AuthTokenPayload structure
 *
 * @param payload - Unknown payload to validate
 * @returns true if payload has valid token structure
 */
export function isValidAuthTokenPayload(payload: unknown): payload is AuthTokenPayload {
  if (!payload || typeof payload !== 'object') return false;

  const p = payload as Record<string, unknown>;

  // Must have accessToken and refreshToken as strings
  if (typeof p.accessToken !== 'string' || !p.accessToken) return false;
  if (typeof p.refreshToken !== 'string' || !p.refreshToken) return false;

  // expiresAt is optional but must be number if present
  if (p.expiresAt !== undefined && typeof p.expiresAt !== 'number') return false;

  return true;
}

/**
 * Type guard to validate AuthStatePayload structure
 *
 * @param payload - Unknown payload to validate
 * @returns true if payload has valid auth state structure
 */
export function isValidAuthStatePayload(payload: unknown): payload is AuthStatePayload {
  if (!payload || typeof payload !== 'object') return false;

  const p = payload as Record<string, unknown>;

  // Must have isAuthenticated boolean
  if (typeof p.isAuthenticated !== 'boolean') return false;

  // Optional fields must be correct type if present
  if (p.userId !== undefined && typeof p.userId !== 'string') return false;
  if (p.email !== undefined && typeof p.email !== 'string') return false;
  if (p.role !== undefined && typeof p.role !== 'string') return false;

  return true;
}

/**
 * Create typed message helpers for common operations
 */
export const IframeMessages = {
  /** Signal that iframe is ready to receive auth tokens */
  ready: () => createMessage('IFRAME_READY'),

  /** Request auth tokens from parent */
  requestToken: () => createMessage('AUTH_TOKEN_REQUEST'),

  /** Request parent to initiate login flow */
  requestLogin: () => createMessage('AUTH_LOGIN_REQUEST'),

  /** Request parent to handle logout */
  requestLogout: () => createMessage('AUTH_LOGOUT_REQUEST'),

  /** Notify parent of auth state change */
  stateChange: (state: AuthStatePayload) =>
    createMessage('AUTH_STATE_CHANGE', state),

  /** Response with auth tokens (sent by parent) */
  tokenResponse: (tokens: AuthTokenPayload) =>
    createMessage('AUTH_TOKEN_RESPONSE', tokens),
};
